import axios from 'axios';
import { setToken, logout } from '../features/auth/authSlice';
import deviceService from '../services/deviceService';

const axiosInstance = axios.create({
  baseURL: 'https://server.survill.com',
  withCredentials: true,
});

// ─────────────────────────────────────────────────────────────────────────────
// Token helpers
// ─────────────────────────────────────────────────────────────────────────────

let storeRef = null;

// Decode a JWT's exp claim (seconds since epoch) without verifying it.
const getTokenExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

// Consider a token "fresh" if it has more than 30s of life left.
const isTokenFresh = (token) => {
  const exp = token && getTokenExp(token);
  return !!exp && exp * 1000 - Date.now() > 30_000;
};

// Single-flight refresh: no matter how many callers need a refresh at the
// same moment (a dozen parallel requests on page load, the WebSocket, a 401
// retry), exactly ONE network refresh happens and everyone awaits it.
let refreshPromise = null;

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    // Device credentials (optional — backend allows admins without them).
    // deviceService caches agent unavailability, so this fails fast instead
    // of adding a blocked localhost round-trip to every refresh.
    let deviceCredentials = null;
    try {
      deviceCredentials = await deviceService.getDeviceCredentials();
    } catch {
      // Fine — backend decides whether this user needs device credentials.
    }

    const refreshPayload = { refresh: refreshToken };
    if (deviceCredentials) {
      refreshPayload.device_key = deviceCredentials.device_key;
      refreshPayload.mac_address = deviceCredentials.mac_address;
      refreshPayload.computer_name = deviceCredentials.computer_name;
    }

    // Plain axios (not axiosInstance) to avoid interceptor loops.
    const response = await axios.post(
      'https://server.survill.com/api/token/refresh/',
      refreshPayload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { access } = response.data;
    localStorage.setItem('accessToken', access);
    if (storeRef) storeRef.dispatch(setToken(access));
    return access;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// Returns a valid access token, refreshing it first if it's expired or about
// to expire. Used by the request interceptor AND the WebSocket reconnect
// logic so sockets never dial with a dead token.
export async function ensureFreshToken() {
  const current = localStorage.getItem('accessToken');
  if (isTokenFresh(current)) return current;
  if (!localStorage.getItem('refreshToken')) return current;
  try {
    return await refreshAccessToken();
  } catch {
    return current; // let the request fail with 401 and the normal flow handle it
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Interceptors
// ─────────────────────────────────────────────────────────────────────────────

// PROACTIVE refresh: check expiry BEFORE each request instead of letting a
// dozen requests 401 on page load and all retry. One refresh, zero 401 burst.
axiosInstance.interceptors.request.use(async (config) => {
  if (config.url?.includes('/api/token/')) return config; // never for auth endpoints
  const token = await ensureFreshToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// REACTIVE fallback: if a 401 still slips through (revoked token, clock skew),
// refresh once and retry the request.
export const setupInterceptors = (store) => {
  storeRef = store;

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (originalRequest.url?.includes('/api/token/refresh/')) {
          store.dispatch(logout());
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const access = await refreshAccessToken(); // single-flight
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          store.dispatch(logout());
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;